/*eslint-disable*/
import React, { useState, useMemo } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import {
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  TableSortLabel,
} from '@mui/material'

// constants
import { MAX_ALLOWED_USERS } from './membersTable.constants'

// helpers
import {
  isMemberSelected,
  areAllMembersChecked,
  createMember,
  getUserIds,
  getSelectedMembersWithOutSelectedMemberId,
  shouldDisableSelectAll,
  shouldDisableUser,
  sortUsers,
} from './membersTable.helpers'
import {
  getFirstName,
  getEmail,
  getFirstLetterOfLastName,
} from '../../../../../../helpers/users'

// styles
import styles from './membersTable.module.scss'

const MembersTable = ({
  group,
  users,
  selectedMembers = [],
  setSelectedMembers,
  areUsersLoading,
  getSelectedMembersCount,
}) => {
  const allMembersChecked = areAllMembersChecked(users, selectedMembers)
  const { t } = useTranslation()

  const [sortBy, setSortBy] = useState('firstName')
  const [sortOrder, setSortOrder] = useState('asc')

  const handleSort = (property) => {
    const newOrder = sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc'
    setSortBy(property)
    setSortOrder(newOrder)
  }

  const handleCheckboxChange = (user) => {
    let updatedSelectedMembers = []
    const selectedMemberId = user?.id
    const isExistingMember =
      group?.members?.some((member) => member.value === selectedMemberId) ||
      false

    if (isExistingMember) {
      return
    }

    if (isMemberSelected(selectedMemberId, selectedMembers)) {
      updatedSelectedMembers = getSelectedMembersWithOutSelectedMemberId(
        selectedMembers,
        selectedMemberId,
      )
    } else {
      updatedSelectedMembers = [...selectedMembers, createMember(user)]
    }
    const selectedMembersCount = getSelectedMembersCount(updatedSelectedMembers)
    if (selectedMembersCount >= MAX_ALLOWED_USERS) {
      toast.warning(t('members-table-cannot-add-remove-more-than-five'))
    }
    setSelectedMembers(updatedSelectedMembers)
  }

  const handleSelectAllCheckBoxChange = () => {
    const allMembersChecked = areAllMembersChecked(users, selectedMembers)
    let updatedSelectedMembers = []
    const userIds = getUserIds(users)
    if (allMembersChecked) {
      updatedSelectedMembers = selectedMembers.filter(
        (selectedMember) => !userIds.includes(selectedMember?.value),
      )
    } else {
      updatedSelectedMembers = [...selectedMembers]
      users.forEach((user) => {
        const isExistingMember =
          group?.members?.some((member) => member.value === user?.id) || false
        if (!isExistingMember && !isMemberSelected(user?.id, selectedMembers)) {
          updatedSelectedMembers.push(createMember(user))
        }
      })
    }
    setSelectedMembers(updatedSelectedMembers)
  }

  const sortedUsers = useMemo(
    () => sortUsers({ users, sortBy, sortOrder }),
    [users, sortBy, sortOrder],
  )

  const renderTable = () => (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '10%' }} className={styles.tableHeader}>
            <TableSortLabel
              active={sortBy === 'firstName'}
              direction={sortOrder}
              onClick={() => handleSort('firstName')}
            >
              {t('edit-members-modal-members-name-table')}
            </TableSortLabel>
          </TableCell>
          <TableCell sx={{ width: '10%' }} className={styles.tableHeader}>
            <TableSortLabel
              active={sortBy === 'lastName'}
              direction={sortOrder}
              onClick={() => handleSort('lastName')}
            >
              {t('members-table-email')}
            </TableSortLabel>
          </TableCell>
          <TableCell sx={{ width: '10%' }} className={styles.tableHeader}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allMembersChecked}
                  onChange={handleSelectAllCheckBoxChange}
                  disabled={shouldDisableSelectAll({
                    users,
                    selectedMembers,
                    getSelectedMembersCount,
                    group,
                  })}
                  sx={{
                    color: shouldDisableSelectAll({
                      users,
                      selectedMembers,
                      getSelectedMembersCount,
                      group,
                    })
                      ? 'grey'
                      : '#5E5047',
                    '&.Mui-checked': {
                      color: shouldDisableSelectAll({
                        users,
                        selectedMembers,
                        getSelectedMembersCount,
                        group,
                      })
                        ? 'grey'
                        : '#000000',
                    },
                  }}
                />
              }
            />
            {t('members-table-select-all')}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedUsers.map((user) => {
          const isExistingMember =
            group?.members?.some((member) => member.value === user.id) || false

          const isDisabled =
            isExistingMember ||
            shouldDisableUser({
              user,
              selectedMembers,
              getSelectedMembersCount,
              group,
            })
          return (
            <TableRow key={user.id}>
              <TableCell sx={{ fontFamily: 'Johnson Text' }}>
                {`${getFirstName(user)} ${getFirstLetterOfLastName(user)}`}
              </TableCell>
              <TableCell sx={{ fontFamily: 'Johnson Text' }}>
                {getEmail(user)}
              </TableCell>
              <TableCell sx={{ width: '10%' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={
                        isExistingMember ||
                        isMemberSelected(user.id, selectedMembers)
                      }
                      onChange={() => handleCheckboxChange(user)}
                      disabled={
                        isExistingMember ||
                        shouldDisableUser({
                          user,
                          selectedMembers,
                          getSelectedMembersCount,
                          group,
                        })
                      }
                      sx={{
                        color: isDisabled
                          ? 'grey'
                          : '#5E5047',
                        '&.Mui-checked': {
                          color: isDisabled
                            ? 'grey'
                            : '#000000',
                        },
                        zIndex: 1,
                      }}
                    />
                  }
                />
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )

  if (areUsersLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          marginBottom: '40px',
        }}
      >
        <CircularProgress sx={{ color: '#000000' }} />
      </div>
    )
  }
  if (sortedUsers?.length > 0) {
    return (
      <TableContainer className={styles.container}>
        {renderTable()}
      </TableContainer>
    )
  }
  return null
}

export default MembersTable
